// Selector genérico de una evidencia YA en el servidor. Lo usan el correo (para
// adjuntar sin ZIP) y la bitácora (para citar una foto sin volver a subirla):
// la lista, las miniaturas y el «no elegible, por esto» son los mismos; cada
// caller decide qué es elegible y qué hace al elegir.
import { useEffect, useState } from "react";
import { ApiError, api, type EvidenciaOut } from "@/lib/api";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";
import { fmtFechaCargaLarga } from "@/lib/fechas";
import { fmtBytes } from "@/lib/formato";

export type Elegibilidad = { ok: boolean; motivo?: string };

type Props = {
  casoId: number;
  onClose: () => void;
  titulo: string;
  descripcion: string;
  /** Qué evidencias se pueden elegir; el `motivo` se muestra en gris en las que no. */
  elegible: (ev: EvidenciaOut) => Elegibilidad;
  onElegir: (ev: EvidenciaOut) => void;
  /** Ids que ya están tomados por el caller (se marcan y no se vuelven a elegir). */
  yaElegidas?: number[];
  /** Si es true, el modal no se cierra al elegir (para tomar varias). */
  multiple?: boolean;
};

export default function SelectorEvidencias({
  casoId,
  onClose,
  titulo,
  descripcion,
  elegible,
  onElegir,
  yaElegidas = [],
  multiple = false,
}: Props) {
  const toast = useToast();
  const [items, setItems] = useState<EvidenciaOut[] | null>(null);

  useEffect(() => {
    api
      .get<EvidenciaOut[]>(`/api/casos/${casoId}/evidencias`)
      .then(setItems)
      .catch((e) => {
        toast.error("No pude cargar las evidencias", e instanceof ApiError ? e.detail : "Error");
        setItems([]);
      });
    // `toast` cambia de referencia en cada render (bucle ya visto en ProveedorPendiente).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casoId]);

  const tomadas = new Set(yaElegidas);

  return (
    <Modal open onOpenChange={(v) => !v && onClose()} title={titulo} description={descripcion} size="lg">
      {items === null ? (
        <p className="text-sm text-zinc-400">Cargando…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-zinc-400">Este caso no tiene evidencias.</p>
      ) : (
        <ul className="scroll-sutil grid min-h-[12rem] flex-1 grid-cols-1 gap-2 overflow-auto sm:grid-cols-2">
          {items.map((ev) => {
            const yaEsta = tomadas.has(ev.id);
            const { ok, motivo } = yaEsta ? { ok: false, motivo: "Ya está en la nota" } : elegible(ev);
            const esImagen = ev.mime.startsWith("image/");
            return (
              <li key={ev.id}>
                <button
                  type="button"
                  disabled={!ok}
                  onClick={() => {
                    onElegir(ev);
                    if (!multiple) onClose();
                  }}
                  className={`w-full flex items-center gap-3 rounded border px-3 py-2 text-left transition ${
                    ok
                      ? "border-ink-700 bg-ink-800 hover:bg-ink-700"
                      : "border-ink-800 bg-ink-900 opacity-50 cursor-not-allowed"
                  }`}
                >
                  {esImagen && !ev.archivo_eliminado_at ? (
                    <img
                      src={`/api/evidencias/${ev.id}/file`}
                      alt=""
                      className="h-10 w-10 rounded object-cover shrink-0"
                    />
                  ) : (
                    <span className="text-xl shrink-0">{esImagen ? "🖼️" : "📄"}</span>
                  )}
                  <span className="min-w-0">
                    {/* Nombre completo (se parte) y fecha con hora a la vista: antes
                        iban cortados y en un tooltip, invisible en el celular. */}
                    <span className="block text-sm text-zinc-100 break-words [overflow-wrap:anywhere]">
                      {ev.nombre_original}
                    </span>
                    <span className="block text-xs text-zinc-500">
                      cargada el {fmtFechaCargaLarga(ev.created_at)} · {fmtBytes(ev.size_bytes)}
                      {!ok && motivo ? ` · ${motivo}` : ""}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
